/**
 * Billing Contract — typed API contract for billing, usage, and plan management.
 *
 * All DTO types derived from generated Zod schemas (OpenAPI-first).
 * Frontend camelCase types and mappers live in the endpoint file.
 * Maps to: core/app/schemas/billing/
 */

import { z } from 'zod';

import {
  zBillingMode as GeneratedBillingMode,
  zEnforcementMode as GeneratedEnforcementMode,
  zPlanTier as GeneratedPlanTier,
  zSubscriptionStatus as GeneratedSubscriptionStatus,
  zLimitsRead as GeneratedLimitsRead,
  zBillingPlanRead as GeneratedBillingPlanRead,
  zBillingSubscriptionRead as GeneratedBillingSubscriptionRead,
  zBillingPlanResponse as GeneratedBillingPlanResponse,
  zBillingPortalResponse as GeneratedBillingPortalResponse,
  zCheckoutSessionResponse as GeneratedCheckoutSessionResponse,
  zCreateCheckoutSessionRequest as GeneratedCreateCheckoutSessionRequest,
  zSeatUpdateRequest as GeneratedSeatUpdateRequest,
  zSeatUpdateResponse as GeneratedSeatUpdateResponse,
  zSeatsUsage as GeneratedSeatsUsage,
  zAiBucketUsage as GeneratedAiBucketUsage,
  zExtractionBucketUsage as GeneratedExtractionBucketUsage,
  zStorageBucketUsage as GeneratedStorageBucketUsage,
  zBucketsUsage as GeneratedBucketsUsage,
  zUsageResponse as GeneratedUsageResponse,
  zUsagePeriodSummary as GeneratedUsagePeriodSummary,
  zUsageHistoryResponse as GeneratedUsageHistoryResponse,
  zPlanCatalogResponse as GeneratedPlanCatalogResponse,
  zPlanCatalogEntry as GeneratedPlanCatalogEntry,
  zPlanCatalogFeatures as GeneratedPlanCatalogFeatures,
  zPlanCatalogLimits as GeneratedPlanCatalogLimits,
  zFeatureMatrixValue as GeneratedFeatureMatrixValue,
  zFeatureMatrixFeature as GeneratedFeatureMatrixFeature,
  zFeatureMatrixCategory as GeneratedFeatureMatrixCategory,
  zCatalogMetadata as GeneratedCatalogMetadata,
} from '@/api/generated/hey-api/zod.gen';

// ─── Enum Schemas ───────────────────────────────────────────────────────────

export const BillingModeSchema = GeneratedBillingMode;
export type BillingMode = z.infer<typeof BillingModeSchema>;

export const EnforcementModeSchema = GeneratedEnforcementMode;
export type EnforcementMode = z.infer<typeof EnforcementModeSchema>;

export const PlanTierSchema = GeneratedPlanTier;
export type PlanTier = z.infer<typeof PlanTierSchema>;

export const SubscriptionStatusSchema = GeneratedSubscriptionStatus;
export type SubscriptionStatus = z.infer<typeof SubscriptionStatusSchema>;

// ─── Read Schemas ───────────────────────────────────────────────────────────

export const LimitsReadSchema = GeneratedLimitsRead;
export type LimitsRead = z.infer<typeof LimitsReadSchema>;

export const BillingPlanReadSchema = GeneratedBillingPlanRead;
export type BillingPlanRead = z.infer<typeof BillingPlanReadSchema>;

export const BillingSubscriptionReadSchema = GeneratedBillingSubscriptionRead;
export type BillingSubscriptionRead = z.infer<typeof BillingSubscriptionReadSchema>;

export const BillingPlanResponseSchema = GeneratedBillingPlanResponse;
export type BillingPlanResponse = z.infer<typeof BillingPlanResponseSchema>;

// ─── Usage Schemas ──────────────────────────────────────────────────────────

export const AiBucketUsageSchema = GeneratedAiBucketUsage;
export type AiBucketUsage = z.infer<typeof AiBucketUsageSchema>;

export const ExtractionBucketUsageSchema = GeneratedExtractionBucketUsage;
export type ExtractionBucketUsage = z.infer<typeof ExtractionBucketUsageSchema>;

export const StorageBucketUsageSchema = GeneratedStorageBucketUsage;
export type StorageBucketUsage = z.infer<typeof StorageBucketUsageSchema>;

export const BucketsUsageSchema = GeneratedBucketsUsage;
export type BucketsUsage = z.infer<typeof BucketsUsageSchema>;

export const SeatsUsageSchema = GeneratedSeatsUsage;
export type SeatsUsage = z.infer<typeof SeatsUsageSchema>;

export const UsageResponseSchema = GeneratedUsageResponse;
export type UsageResponse = z.infer<typeof UsageResponseSchema>;

export const UsagePeriodSummarySchema = GeneratedUsagePeriodSummary;
export type UsagePeriodSummary = z.infer<typeof UsagePeriodSummarySchema>;

export const UsageHistoryResponseSchema = GeneratedUsageHistoryResponse;
export type UsageHistoryResponse = z.infer<typeof UsageHistoryResponseSchema>;

// ─── Request/Response Schemas ───────────────────────────────────────────────

export const CreateCheckoutSessionRequestSchema = GeneratedCreateCheckoutSessionRequest;
export type CreateCheckoutSessionRequest = z.infer<typeof CreateCheckoutSessionRequestSchema>;

export const CheckoutSessionResponseSchema = GeneratedCheckoutSessionResponse;
export type CheckoutSessionResponse = z.infer<typeof CheckoutSessionResponseSchema>;

export const BillingPortalResponseSchema = GeneratedBillingPortalResponse;
export type BillingPortalResponse = z.infer<typeof BillingPortalResponseSchema>;

export const SeatUpdateRequestSchema = GeneratedSeatUpdateRequest;
export type SeatUpdateRequest = z.infer<typeof SeatUpdateRequestSchema>;

export const SeatUpdateResponseSchema = GeneratedSeatUpdateResponse;
export type SeatUpdateResponse = z.infer<typeof SeatUpdateResponseSchema>;

// ─── Plan Catalog Schemas ───────────────────────────────────────────────────

export const PlanCatalogFeaturesSchema = GeneratedPlanCatalogFeatures;
export type PlanCatalogFeatures = z.infer<typeof PlanCatalogFeaturesSchema>;

export const PlanCatalogLimitsSchema = GeneratedPlanCatalogLimits;
export type PlanCatalogLimits = z.infer<typeof PlanCatalogLimitsSchema>;

export const PlanCatalogEntrySchema = GeneratedPlanCatalogEntry;
export type PlanCatalogEntry = z.infer<typeof PlanCatalogEntrySchema>;

export const PlanCatalogResponseSchema = GeneratedPlanCatalogResponse;
export type PlanCatalogResponse = z.infer<typeof PlanCatalogResponseSchema>;

export const FeatureMatrixValueSchema = GeneratedFeatureMatrixValue;
export type FeatureMatrixValue = z.infer<typeof FeatureMatrixValueSchema>;

export const FeatureMatrixFeatureSchema = GeneratedFeatureMatrixFeature;
export type FeatureMatrixFeature = z.infer<typeof FeatureMatrixFeatureSchema>;

export const FeatureMatrixCategorySchema = GeneratedFeatureMatrixCategory;
export type FeatureMatrixCategory = z.infer<typeof FeatureMatrixCategorySchema>;

export const CatalogMetadataSchema = GeneratedCatalogMetadata;
export type CatalogMetadata = z.infer<typeof CatalogMetadataSchema>;

// ─── Add-On Types (manual until OpenAPI codegen runs) ───────────────────────

export type AddOnType = 'one_time_boost' | 'recurring';
export type AddOnStatus = 'active' | 'expired' | 'canceled';

export interface AddOnCatalogEntry {
  product_key: string;
  name: string;
  description: string;
  addon_type: AddOnType;
  bucket: string | null;
  quantity_label: string;
  price_cents: number;
  price_display: string;
  min_plan: string;
}

export interface AddOnRead {
  id: string;
  workspace_id: string;
  addon_type: AddOnType;
  product_key: string;
  bucket: string | null;
  quantity: number;
  unit_label: string;
  price_cents: number;
  period_key: string | null;
  status: AddOnStatus;
  activated_at: string;
  expires_at: string | null;
  canceled_at: string | null;
}

export interface ActiveAddOnsResponse {
  addons: AddOnRead[];
  effects: Record<string, number>;
}

export interface AddOnCheckoutResponse {
  checkout_url: string | null;
  addon_id: string | null;
}

export interface AddOnRecommendationDTO {
  product_key: string;
  name: string;
  reason: string;
  projected_days_extension: number;
  price_display: string;
}
